/**
 * Tech Ops → Bidso Reviews tab.
 *
 * Lists every Bidso SKU currently in PENDING_REVIEW or SENT_BACK status.
 * Each row is a `<BidsoReviewRow>` that owns its own expansion + BOM editor
 * (so multiple rows can be expanded independently). Separation of duties
 * (submitter cannot review own commit, master_admin bypasses) is enforced on
 * the backend; this tab mirrors it by surfacing `is_self_submit` to the row.
 *
 * Wired to:
 *   GET /api/bidso-composer/tech-ops/requests
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Check, Search, RefreshCw, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import BidsoReviewRow from "./BidsoReviewRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BidsoReviewsTab() {
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [sentBackCount, setSentBackCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("PENDING_REVIEW"); // "ALL" | "PENDING_REVIEW" | "SENT_BACK"
  const [search, setSearch] = useState("");
  // Incremented to force a re-fetch (Refresh button + after each row action).
  const [refreshNonce, setRefreshNonce] = useState(0);

  const token = (() => {
    try { return JSON.parse(localStorage.getItem("auth-storage"))?.state?.token; } catch { return null; }
  })();

  useEffect(() => {
    let cancel = false;
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/bidso-composer/tech-ops/requests`, { headers: authHeaders });
        if (cancel) return;
        setItems(res.data?.items || []);
        setPendingCount(res.data?.pending_count || 0);
        setSentBackCount(res.data?.sent_back_count || 0);
      } catch (e) {
        if (cancel) return;
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Failed to load review queue");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token, refreshNonce]);

  const triggerRefresh = () => {
    setRefreshNonce((n) => n + 1);
    // Tell other parts of the app (Composer landing, SKU Management) to refresh.
    window.dispatchEvent(new Event("bidso-composer:refresh"));
  };

  // Client-side filter + search.
  const visible = items.filter((r) => {
    if (filter !== "ALL" && r.status !== filter) return false;
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return (
      (r.bidso_sku_id || "").toLowerCase().includes(s) ||
      (r.name || "").toLowerCase().includes(s) ||
      (r.submitted_by_name || "").toLowerCase().includes(s) ||
      (r.source_bidso_sku_id || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-4" data-testid="bidso-reviews-tab">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-violet-600"/>
            Bidso SKU Reviews
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Click a SKU to open its BOM. Edit quantities, remove rows, or add RMs from the repository, then Approve or Send back.
            You cannot review your own submissions unless you are master admin.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={triggerRefresh} disabled={loading} data-testid="reviews-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}/>
          Refresh
        </Button>
      </div>

      {/* Filter pills + search */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant={filter === "PENDING_REVIEW" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("PENDING_REVIEW")}
          className={filter === "PENDING_REVIEW" ? "bg-amber-600 hover:bg-amber-700" : ""}
          data-testid="filter-pending"
        >
          Pending Review <Badge variant="secondary" className="ml-2">{pendingCount}</Badge>
        </Button>
        <Button
          variant={filter === "SENT_BACK" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("SENT_BACK")}
          className={filter === "SENT_BACK" ? "bg-red-600 hover:bg-red-700" : ""}
          data-testid="filter-sent-back"
        >
          Sent Back <Badge variant="secondary" className="ml-2">{sentBackCount}</Badge>
        </Button>
        <Button
          variant={filter === "ALL" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilter("ALL")}
          data-testid="filter-all"
        >
          All ({pendingCount + sentBackCount})
        </Button>
        <div className="ml-auto relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400"/>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU, name, submitter…"
            className="pl-9 h-9"
            data-testid="reviews-search"
          />
        </div>
      </div>

      {/* Queue */}
      {loading && items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-zinc-500">Loading review queue…</CardContent></Card>
      ) : visible.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center" data-testid="reviews-empty">
            <Check className="w-10 h-10 text-emerald-500 mx-auto mb-2"/>
            <div className="font-medium">Nothing to review</div>
            <div className="text-sm text-zinc-500 mt-1">
              {filter === "PENDING_REVIEW" && "No Bidso SKUs are waiting for approval."}
              {filter === "SENT_BACK" && "No SKUs are currently in SENT_BACK state."}
              {filter === "ALL" && "The review queue is empty."}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {visible.map((row) => (
            <BidsoReviewRow
              key={row.bidso_sku_id}
              row={row}
              token={token}
              onActionDone={triggerRefresh}
            />
          ))}
        </div>
      )}
    </div>
  );
}
